import React from 'react';
import { NavigateFunction, useLocation, Navigate, useRoutes, RouteObject } from 'react-router-dom';

import { Routes } from 'config/constants';
import AccountPage from 'pages/Account';
import ApiKeysPage from 'pages/ApiKeys';
import RequestsPage from 'pages/Requests';
import GraphqlPage from 'pages/Graphql';
import ResourcesPage from 'pages/Resources';
import OrdersPage from 'pages/Orders';
import { AffiliateDisclosurePage } from 'pages/AffiliateDisclosure';
import LoginPage from 'pages/Auth/Login';
import RegisterPage from 'pages/Auth/Register';
import BillingPage from 'pages/Billing';
import DrawdownBillingPage from 'pages/DrawdownBilling';
import DrawdownSetupPage from 'pages/DrawdownSetup';
import NoAccessPage from 'pages/NoAccess';
import TeamPage from 'pages/Team';

import ResetPage from 'pages/Auth/Reset';
import PaymentPage from 'pages/Payment';
import { PrivateRoute } from 'routes/PrivateRoute';
import { User } from 'firebase/auth';
import { OrderDetails } from 'components/Orders/orderDetails';
import DashboardPage from 'pages/Dashboard';
import CheckoutIntentsPage from 'pages/CheckoutIntents';
import { LoaderWrapper } from 'components/LoaderWrapper';
import { useFlags } from 'providers/FlagsProvider';
import CreateOrderPage from 'pages/CreateOrder';
import CommissionsPage from 'pages/Commissions';
import MerchantsPage from 'pages/Merchants';
import CatalogPage from 'pages/Catalog';
import { usBankAccountPaymentMethodOptions } from 'pages/Billing/paymentMethodOptions';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { isLegacyBilling } from 'lib/utils';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { env } from 'config/env';
import { canAccessRoute, getDefaultAccessibleRoute } from 'app/access';

const stripePromise = loadStripe(env.VITE_STRIPE_KEY);

export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

export const defaultLoginRedirectFlow = (
  navigate: NavigateFunction,
  loading: boolean,
  user: User | null | undefined,
  queryRedirect?: string | null,
  fallback?: () => void,
  search?: string,
): void => {
  if (loading) {
    // do nothing
  } else if (user && queryRedirect) {
    const urlParams = new URLSearchParams(search);
    urlParams.delete('redirect');
    navigate({ pathname: queryRedirect, search: urlParams.toString() }, { replace: true });
  } else if (fallback) {
    fallback();
  }
};

const DefaultRouteRedirect = () => {
  const { teamTabEnabled } = useFlags();
  const userInfo = useAppSelector(selectUserInfo);
  const defaultRoute = userInfo
    ? getDefaultAccessibleRoute(userInfo, {
        disabledRoutes: teamTabEnabled ? [] : [Routes.Team],
      })
    : Routes.Dashboard;
  return <Navigate to={defaultRoute} replace />;
};

const ROUTES: RouteObject[] = [
  // Legal-related pages
  { path: Routes.Affiliate, element: <AffiliateDisclosurePage /> },

  // Auth-related pages
  { path: Routes.Login, element: <LoginPage /> },
  { path: Routes.Register, element: <RegisterPage /> },
  { path: Routes.Reset, element: <ResetPage /> },

  // Re-route / to /dashboard
  {
    path: Routes.All,
    element: <DefaultRouteRedirect />,
  },

  // Navbar links
  {
    path: Routes.Dashboard,
    element: (
      <PrivateRoute>
        <LoaderWrapper>
          <DashboardPage />
        </LoaderWrapper>
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Account,
    element: (
      <PrivateRoute>
        <AccountPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.ApiKeys,
    element: (
      <PrivateRoute>
        <ApiKeysPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Requests,
    element: (
      <PrivateRoute>
        <RequestsPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Orders,
    element: (
      <PrivateRoute>
        <OrdersPage />
      </PrivateRoute>
    ),
  },
  // Payment page
  {
    path: Routes.Payment,
    element: (
      <PrivateRoute>
        <Elements
          stripe={stripePromise}
          options={{
            mode: 'payment',
            amount: 100,
            currency: 'usd',
            paymentMethodCreation: 'manual',
            paymentMethodTypes: ['us_bank_account', 'card'],
            paymentMethodOptions: usBankAccountPaymentMethodOptions,
            appearance: {
              theme: 'flat',
              labels: 'floating',
              variables: { colorPrimary: '#88aa4a', borderRadius: '12px' },
            },
          }}
        >
          <PaymentPage />
        </Elements>
      </PrivateRoute>
    ),
  },
  {
    path: Routes.DrawdownSetup,
    element: (
      <PrivateRoute>
        <DrawdownSetupPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.NoAccess,
    element: (
      <PrivateRoute>
        <NoAccessPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Merchants,
    element: (
      <PrivateRoute>
        <MerchantsPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Graphql,
    element: (
      <PrivateRoute fitted>
        <GraphqlPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Billing,
    element: (
      <PrivateRoute>
        <BillingPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Team,
    element: (
      <PrivateRoute>
        <TeamPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Commissions,
    element: (
      <PrivateRoute>
        <CommissionsPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Commission,
    element: (
      <PrivateRoute>
        <CommissionsPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Resources,
    element: (
      <PrivateRoute>
        <ResourcesPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.CreateOrder,
    element: (
      <PrivateRoute>
        <CreateOrderPage />
      </PrivateRoute>
    ),
  },
  {
    path: Routes.Catalog,
    element: (
      <PrivateRoute fitted>
        <CatalogPage />
      </PrivateRoute>
    ),
  },
  // Order details page
  {
    path: Routes.Order,
    element: (
      <PrivateRoute>
        <OrderDetails />
      </PrivateRoute>
    ),
  },
  {
    // Optional `:id` so the same component handles both the list view and a
    // deep-linked intent. When `id` is present the detail sheet auto-opens.
    path: `${Routes.CheckoutIntents}/:id?`,
    element: (
      <PrivateRoute>
        <CheckoutIntentsPage />
      </PrivateRoute>
    ),
  },
];

const MainRouter = () => {
  const { teamTabEnabled, viewCreateOrder } = useFlags();
  let routes = ROUTES.slice();
  const userInfo = useAppSelector(selectUserInfo);
  const { billingPeriodSettings } = userInfo || {};

  const isSubscriptionBilling = !isLegacyBilling(billingPeriodSettings);

  if (isSubscriptionBilling) {
    routes = routes.filter(
      (route) =>
        route.path !== Routes.Graphql &&
        route.path !== Routes.Resources &&
        route.path !== Routes.Orders &&
        route.path !== Routes.Order &&
        route.path !== Routes.Merchants,
    );

    // Swap legacy billing page for drawdown billing page
    routes = routes.map((route) =>
      route.path === Routes.Billing
        ? {
            ...route,
            element: (
              <PrivateRoute>
                <DrawdownBillingPage />
              </PrivateRoute>
            ),
          }
        : route,
    );
  } else if (billingPeriodSettings) {
    // Commissions ship with the v2/subscription system only. Only strip the
    // route once billing info has actually loaded — otherwise a direct hit to
    // /commissions during the load window falls through to the wildcard route
    // and redirects to /dashboard.
    routes = routes.filter(
      (route) => route.path !== Routes.Commissions && route.path !== Routes.Commission,
    );
  }

  if (viewCreateOrder && isSubscriptionBilling) {
    routes = routes.filter((route) => route.path !== Routes.CreateOrder);
  }

  if (!teamTabEnabled) {
    routes = routes.filter((route) => route.path !== Routes.Team);
  }

  if (userInfo) {
    routes = routes.filter(
      (route) => route.path === Routes.NoAccess || canAccessRoute(userInfo, route.path?.toString()),
    );
  }

  return useRoutes(routes);
};

export default MainRouter;
